import { useCallback, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import AuthService from "@/services/auth-service";
import { getMessageHeaders } from "@/utils/utils";

const basePath =
    (import.meta.env.VITE_ILLUM_API as string | undefined) ??
    "https://illuminatewebdev.jetcharge.com.au/api";

// NOTE: the generated useAiChatbotQuery hook (src/api/ai-chatbot-http-functions.ts) is
// used directly for the non-streaming path when the IL_4148_STREAM_CHATBOT_RESPONSES
// flag is off. The generated useAiChatbotQueryStream hook
// (src/api/ai-chatbot-stream-http-functions.ts) is never used by the UI — it can't
// express an SSE response over fetch, so this hook streams the query-stream endpoint
// directly instead. That generated hook is kept in sync purely for the OpenAPI diff
// check in CI (azure-pipelines-templates/verify-openapi.yml).
export const useAiChatbotStream = () => {
    const { instance } = useMsal();
    const [streamedText, setStreamedText] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const reset = useCallback(() => {
        setStreamedText("");
        setIsStreaming(false);
        setError(null);
    }, []);

    const start = useCallback(
        async (question: string) => {
            reset();
            setIsStreaming(true);

            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            const fetchUrl = new URL(
                "./ai-chatbot/query-stream",
                basePath.endsWith("/") ? basePath : `${basePath}/`,
            );

            try {
                await fetchEventSource(fetchUrl.toString(), {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...getMessageHeaders(),
                        Authorization: !import.meta.env["STORYBOOK"]
                            ? await AuthService.getAuthorizationHeader(instance)
                            : "",
                    },
                    body: JSON.stringify({ question }),
                    signal: controller.signal,
                    onmessage(ev) {
                        if (ev.event === "error") {
                            throw new Error(JSON.parse(ev.data) as string);
                        }
                        setStreamedText(
                            (prev) => prev + (JSON.parse(ev.data) as string),
                        );
                    },
                    onerror(err) {
                        // Disable the library's default auto-retry: a mid-stream failure must be
                        // terminal, not silently resend the question after a 1s delay.
                        throw err;
                    },
                });
            } catch (err) {
                if (abortControllerRef.current !== controller) {
                    return;
                }
                if (err instanceof Error && err.name === "AbortError") {
                    return;
                }
                setError(err instanceof Error ? err : new Error(String(err)));
            } finally {
                if (abortControllerRef.current === controller) {
                    setIsStreaming(false);
                }
            }
        },
        [instance, reset],
    );

    return { start, streamedText, isStreaming, error, reset };
};
